import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  acceptGroupInvitation,
  declineGroupInvitation
} from '../../services/groupChatInvitationService';

/**
 * GroupInvitations Component
 *
 * Displays pending group chat invitations and allows users to accept or decline
 */

export default function GroupInvitations({ invitations, onUpdate }) {
  const { user } = useAuth();
  const [processingId, setProcessingId] = useState(null);
  const [error, setError] = useState('');

  const handleAccept = async (invitation) => {
    if (!user) return;

    setProcessingId(invitation.id);
    setError('');

    try {
      await acceptGroupInvitation(invitation.id, user.uid);

      // Call onUpdate to refresh invitations list
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error accepting invitation:', error);
      setError('Failed to accept invitation. Please try again.');
    } finally {
      setProcessingId(null);
    }
  };

  const handleDecline = async (invitation) => {
    if (!user) return;

    setProcessingId(invitation.id);
    setError('');

    try {
      await declineGroupInvitation(invitation.id, user.uid);

      // Call onUpdate to refresh invitations list
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error declining invitation:', error);
      setError('Failed to decline invitation. Please try again.');
    } finally {
      setProcessingId(null);
    }
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '';

    const now = Date.now();
    const inviteTime = timestamp.toMillis ? timestamp.toMillis() : timestamp;
    const diffMs = now - inviteTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  if (!invitations || invitations.length === 0) {
    return null;
  }

  return (
    <div className="mb-6">
      <div className="mb-3 px-2">
        <h3 className="text-white/50 text-xs font-medium uppercase tracking-wider">
          Invitations
        </h3>
      </div>

      {error && (
        <div className="mb-3 mx-2 py-2">
          <p className="text-red-400/80 text-[13px]">{error}</p>
        </div>
      )}

      <div className="space-y-4">
        {invitations.map(invitation => {
          const isProcessing = processingId === invitation.id;

          return (
            <div
              key={invitation.id}
              className="mx-2 py-3 border-b border-white/5"
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>

                <div className="flex-1 min-w-0">
                  <h4 className="text-white font-medium text-[15px] truncate">
                    {invitation.groupName}
                  </h4>
                  <p className="text-white/40 text-[13px] mt-0.5">
                    From {invitation.invitedByName} · {formatTimeAgo(invitation.createdAt)}
                  </p>
                </div>
              </div>

              <div className="flex gap-2 pl-12">
                <button
                  onClick={() => handleAccept(invitation)}
                  disabled={isProcessing}
                  className="px-4 py-2 bg-[#06c] hover:bg-[#0055d4] text-white text-[13px] font-medium rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {isProcessing ? 'Accepting...' : 'Accept'}
                </button>
                <button
                  onClick={() => handleDecline(invitation)}
                  disabled={isProcessing}
                  className="px-4 py-2 text-white/60 hover:text-white/80 text-[13px] font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {isProcessing ? 'Declining...' : 'Decline'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
